import type { ConsentCategory } from './types';
import { getStrictlyNecessary } from '@msc-b2c/shared-utils';
import { getAllConfigs } from './getKeyConfig';

interface DidomiPurposeStatus {
  id: string;
  enabled: boolean;
}

interface DidomiVendorStatus {
  id: string;
  enabled: boolean;
}

interface DidomiCurrentUserStatus {
  purposes: Record<string, DidomiPurposeStatus>;
  vendors: Record<string, DidomiVendorStatus>;
  regulation: string;
}

interface DidomiSDK {
  getCurrentUserStatus: () => DidomiCurrentUserStatus;
  on: (event: string, callback: () => void) => void;
}

function getDidomiGlobal(): DidomiSDK | undefined {
  try {
    return (window as unknown as { Didomi?: DidomiSDK }).Didomi;
  } catch {
    return undefined;
  }
}

export function getDidomi(): DidomiSDK | null {
  const didomi = getDidomiGlobal();
  return didomi && typeof didomi.getCurrentUserStatus === 'function' ? didomi : null;
}

export function isPurposeEnabled(purposeId: string): boolean {
  if (getStrictlyNecessary().includes(purposeId)) return true;
  const didomi = getDidomi();
  if (!didomi) return false;
  try {
    const status = didomi.getCurrentUserStatus();
    return status?.purposes?.[purposeId]?.enabled ?? false;
  } catch {
    return false;
  }
}

export function isVendorEnabled(vendorId: string): boolean {
  const didomi = getDidomi();
  if (!didomi) return false;
  try {
    const status = didomi.getCurrentUserStatus();
    return status?.vendors?.[vendorId]?.enabled ?? false;
  } catch {
    return false;
  }
}

export function isConsentGranted(category: ConsentCategory, vendors: string[]): boolean {
  if (getStrictlyNecessary().includes(category)) return true;
  if (!isPurposeEnabled(category)) return false;
  if (vendors.length === 0) return false;
  return vendors.some((vendorId) => isVendorEnabled(vendorId));
}

export function getDisabledVendors(): string[] {
  const didomi = getDidomi();
  if (!didomi) return [];
  try {
    const status = didomi.getCurrentUserStatus();
    return Object.entries(status?.vendors ?? {})
      .filter(([, v]) => !v.enabled)
      .map(([id]) => id);
  } catch {
    return [];
  }
}

export function getAllConsentStates(): Record<string, boolean> {
  const configs = getAllConfigs();
  const categoryVendorsMap = new Map<ConsentCategory, string[]>();

  for (const cfg of configs) {
    const existing = categoryVendorsMap.get(cfg.category);
    if (existing) {
      for (const v of cfg.vendors) {
        if (!existing.includes(v)) existing.push(v);
      }
    } else {
      categoryVendorsMap.set(cfg.category, [...cfg.vendors]);
    }
  }

  return Object.fromEntries(
    [...categoryVendorsMap.entries()].map(([category, vendors]) => [
      category,
      isConsentGranted(category, vendors),
    ])
  ) as Record<ConsentCategory, boolean>;
}
